import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { AuthLayout } from './AuthLayout';
import { useResetPassword } from './hooks/useAuth';

const schema = z.object({
  email: z.string().min(1).email('Geçerli bir e-posta gir'),
});
type FormData = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const reset = useResetPassword();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (data) => {
    setError(null);
    try {
      await reset.mutateAsync(data.email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    }
  });

  return (
    <AuthLayout title={t('forgot.baslik')} subtitle={t('forgot.alt_baslik')}>
      {sent ? (
        <div>
          <div className="rounded-md border border-success/30 bg-success/5 p-4 text-sm">
            ✉️ {t('forgot.basarili')}
          </div>
          <Link
            to="/auth/login"
            className="mt-6 inline-block text-sm text-primary hover:underline"
          >
            ← {t('forgot.girise_don')}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor={emailId} className="block text-sm font-medium mb-1.5">
              {t('forgot.email')}
            </label>
            <Input
              id={emailId}
              type="email"
              autoComplete="email"
              {...register('email')}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
            )}
          </div>

          {error && (
            <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('forgot.gonderiliyor') : t('forgot.gonder')}
          </Button>

          <Link
            to="/auth/login"
            className="block text-center text-sm text-primary hover:underline"
          >
            ← {t('forgot.girise_don')}
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}
